import React, { useEffect, useState } from 'react';

export type DropSide = 'before' | 'after';
export type DragAxis = 'x' | 'y';

// Drops unknown/duplicate keys and inserts columns missing from `saved` after their default predecessor.
export function normalizeColumnOrder<K extends string>(saved: unknown, defaultOrder: readonly K[]): K[] {
  const known = new Set<string>(defaultOrder);
  const result: K[] = [];
  if (Array.isArray(saved)) {
    saved.forEach((key) => {
      if (typeof key === 'string' && known.has(key) && !result.includes(key as K)) {
        result.push(key as K);
      }
    });
  }
  defaultOrder.forEach((key, idx) => {
    if (result.includes(key)) return;
    let insertAt = 0;
    for (let p = idx - 1; p >= 0; p--) {
      const at = result.indexOf(defaultOrder[p]);
      if (at >= 0) {
        insertAt = at + 1;
        break;
      }
    }
    result.splice(insertAt, 0, key);
  });
  return result;
}

export function moveColumn<K extends string>(order: K[], dragged: K, target: K, side: DropSide): K[] {
  if (dragged === target || !order.includes(dragged) || !order.includes(target)) return order;
  const without = order.filter((key) => key !== dragged);
  const targetIdx = without.indexOf(target);
  without.splice(side === 'before' ? targetIdx : targetIdx + 1, 0, dragged);
  return without;
}

const readOrder = <K extends string>(storageKey: string | null, defaultOrder: readonly K[]): K[] => {
  if (!storageKey) return [...defaultOrder];
  try {
    const raw = window.localStorage.getItem(storageKey);
    return normalizeColumnOrder(raw ? JSON.parse(raw) : null, defaultOrder);
  } catch {
    return [...defaultOrder];
  }
};

const writeOrder = (storageKey: string | null, order: string[] | null): void => {
  if (!storageKey) return;
  try {
    if (order === null) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, JSON.stringify(order));
    }
  } catch {
    // localStorage can be unavailable (private mode, quota); the order still applies for this session.
  }
};

/**
 * Column order with native drag-and-drop, persisted to localStorage under `storageKey`
 * (null disables persistence). `defaultOrder` must be a stable (module-level) array.
 */
export function useDraggableColumnOrder<K extends string>(defaultOrder: readonly K[], storageKey: string | null) {
  const [columnOrder, setColumnOrder] = useState<K[]>(() => readOrder(storageKey, defaultOrder));
  const [dragged, setDragged] = useState<K | null>(null);
  const [dropTarget, setDropTarget] = useState<{ key: K; side: DropSide } | null>(null);

  useEffect(() => {
    setColumnOrder(readOrder(storageKey, defaultOrder));
  }, [storageKey, defaultOrder]);

  const updateColumnOrder = (next: K[]) => {
    setColumnOrder(next);
    writeOrder(storageKey, next);
  };

  const resetColumnOrder = () => {
    setColumnOrder([...defaultOrder]);
    writeOrder(storageKey, null);
  };

  const clearDrag = () => {
    setDragged(null);
    setDropTarget(null);
  };

  const getDragProps = (key: K, axis: DragAxis) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent<HTMLElement>) => {
      setDragged(key);
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        // Firefox won't start a drag without data set.
        e.dataTransfer.setData('text/plain', key);
      }
    },
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      if (!dragged) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      const rect = e.currentTarget.getBoundingClientRect();
      const side: DropSide = axis === 'x'
        ? (e.clientX < rect.left + rect.width / 2 ? 'before' : 'after')
        : (e.clientY < rect.top + rect.height / 2 ? 'before' : 'after');
      if (dropTarget?.key !== key || dropTarget.side !== side) setDropTarget({ key, side });
    },
    onDrop: (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      if (dragged) {
        const side = dropTarget?.key === key ? dropTarget.side : 'before';
        updateColumnOrder(moveColumn(columnOrder, dragged, key, side));
      }
      clearDrag();
    },
    onDragEnd: clearDrag,
  });

  const dragClass = (key: K, axis: DragAxis): string => {
    if (dragged === key) return 'opacity-50';
    if (!dragged || dropTarget?.key !== key) return '';
    if (axis === 'x') {
      return dropTarget.side === 'before' ? 'shadow-[inset_2px_0_0_0_#7F56D9]' : 'shadow-[inset_-2px_0_0_0_#7F56D9]';
    }
    return dropTarget.side === 'before' ? 'shadow-[inset_0_2px_0_0_#7F56D9]' : 'shadow-[inset_0_-2px_0_0_#7F56D9]';
  };

  return { columnOrder, updateColumnOrder, resetColumnOrder, getDragProps, dragClass };
}
